import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
} from '@angular/core';
import { ModalComponent } from '@catch/general-ui';
import { LanguageService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, fromEvent, merge, take } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { UserAdapter } from '../../adapters/user.adapter';
import { UserAccount } from '../../interfaces/user-account';

// Declare HubSpot with proper typing
declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (config: any) => void;
      };
    };
  }
}

@UntilDestroy()
@Component({
  selector: 'app-nps-modal',
  templateUrl: './nps-modal.component.html',
  styleUrls: ['./nps-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent, TranslocoPipe, ButtonComponent],
})
export class NPSModalComponent implements AfterViewInit {
  @ViewChild('npsModal') modal: ModalComponent;
  @ViewChild('npsModalIgnore') modalIgnore: ModalComponent;
  @ViewChild('hubspotForm') formElement: any;
  @Input() userAccount: UserAccount;
  valid = false;
  emailInput;
  scoreInput;
  secondTitle;

  form: 1 | 2 = 1;

  constructor(
    public languageService: LanguageService,
    private userAdapter: UserAdapter,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    if (!this.userAccount.showNPSModal) return;

    const openModal = () => {
      if (!this.modal) setTimeout(() => openModal(), 10);
      else {
        this.modal.open();
        this.openForm1();
      }
    };

    window.addEventListener('message', (event) => {
      if (
        event.data.type === 'hsFormCallback' &&
        event.data.eventName === 'onFormSubmitted'
      ) {
        if (this.form === 2) {
          this.modal.close();
          this.cdr.markForCheck();
          return;
        }
        this.userAdapter
          .submitNPS()
          .pipe(take(1), untilDestroyed(this))
          .subscribe(() => this.openForm2());
      }
    });

    openModal();
  }

  private loadHubSpotScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (typeof window.hbspt !== 'undefined' && window.hbspt?.forms?.create) {
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector(
        'script[src*="hsforms.net"]'
      );
      if (existingScript) {
        // Wait for existing script to load
        const checkHubSpot = () => {
          if (
            typeof window.hbspt !== 'undefined' &&
            window.hbspt?.forms?.create
          ) {
            resolve();
          } else {
            setTimeout(checkHubSpot, 100);
          }
        };
        checkHubSpot();
        return;
      }

      // Load the script dynamically
      const script = document.createElement('script');
      script.src = '//js-eu1.hsforms.net/forms/embed/v2.js';
      script.charset = 'utf-8';
      script.type = 'text/javascript';

      script.onload = () => {
        // Wait a bit for HubSpot to initialize
        setTimeout(() => {
          if (
            typeof window.hbspt !== 'undefined' &&
            window.hbspt?.forms?.create
          ) {
            resolve();
          } else {
            reject(new Error('HubSpot failed to initialize'));
          }
        }, 1000);
      };

      script.onerror = () => {
        reject(new Error('Failed to load HubSpot script'));
      };

      document.head.appendChild(script);
    });
  }

  openForm1() {
    const setInputs = () => {
      if (
        !document.querySelector<any>('input[name="email"]') ||
        !document.querySelector<any>('select[name="nps_kontakt"]')
      )
        setTimeout(setInputs, 10);
      else {
        this.scoreInput = document.querySelector<any>(
          'select[name="nps_kontakt"]'
        );
        this.emailInput = document.querySelector<any>('input[name="email"]');
        this.emailInput.value = this.userAccount.email;
        this.emailInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.emailInput.focus(); //without makes the label sometimes not transition to up position
        this.valid = this.isValid();
        this.cdr.markForCheck();
        merge(
          fromEvent(this.emailInput, 'input'),
          fromEvent(this.scoreInput, 'input')
        )
          .pipe(debounceTime(100), untilDestroyed(this))
          .subscribe(() => {
            this.valid = this.isValid();
            this.cdr.markForCheck();
          });
      }
    };

    const createForm = async () => {
      if (!document.querySelector('div#hubspotForm'))
        setTimeout(() => createForm(), 10);
      else {
        try {
          // Load HubSpot script if not already loaded
          await this.loadHubSpotScript();

          window.hbspt.forms.create({
            region: 'eu1',
            portalId: '26568270',
            formId: 'c89984cf-7802-44b9-8c43-f12698f72f54',
            target: '#hubspotForm',
            locale: this.languageService.currentLanguage.value,
            inlineMessage: ' ', //needed to avoid redirect
          });
          this.cdr.markForCheck();
          setInputs();
        } catch (error) {
          console.warn('HubSpot form creation failed:', error);
        }
      }
    };
    createForm();
  }

  openForm2() {
    this.form = 2;

    const createForm2 = async () => {
      try {
        // Load HubSpot script if not already loaded
        await this.loadHubSpotScript();

        window.hbspt.forms.create({
          region: 'eu1',
          portalId: '26568270',
          formId: '27554e3c-8991-46df-9863-8066dcb9d3f5',
          target: '#hubspotForm',
          locale: this.languageService.currentLanguage.value,
          inlineMessage: ' ', //needed to avoid redirect
        });
        this.cdr.markForCheck();
        const setTitle2 = () => {
          if (
            !document.querySelector<Element>(
              '.hs-form-field.hs-fieldtype-textarea>legend.hs-field-desc'
            )
          )
            setTimeout(setTitle2, 10);
          else {
            this.secondTitle = document.querySelector<Element>(
              '.hs-fieldtype-textarea > legend.hs-field-desc'
            ).innerHTML;
            this.cdr.markForCheck();
          }
        };
        setTitle2();
      } catch (error) {
        console.warn('HubSpot form creation failed:', error);
      }
    };

    createForm2();
  }

  submit() {
    try {
      const hsButton = document.querySelector<any>('input[type="submit"]');
      if (hsButton) {
        hsButton.click(); //click button in form of hubspot
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.warn('HubSpot submit failed:', error);
    }
  }

  ignore() {
    this.userAdapter.ignoreNPS().subscribe(() => {
      // Subscription handled
    });
  }

  isValid() {
    if (this.form === 2) return true;
    if (this.emailInput === undefined || this.scoreInput === undefined)
      return false;
    if (
      this.emailInput.classList.contains('invalid') ||
      this.emailInput.classList.contains('is-placeholder') ||
      this.emailInput.classList.contains('error') ||
      this.scoreInput.classList.contains('invalid') ||
      this.scoreInput.classList.contains('is-placeholder') ||
      this.scoreInput.classList.contains('error')
    )
      return false;
    return true;
  }
}
